import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';
import useMultiOpeningMatStore from '../../../stores/useMultiOpeningMatStore';
import {
  MULTI_OPENING_PRODUCTS,
  PRODUCTS_WITH_5X7,
  PRODUCTS_WITH_8X8,
  findMultiOpeningProductByName,
  isOvalDecklePaper,
  isCircleDecklePaper,
} from '../../../lib/multiOpeningMatProducts';

const isMultiOpeningPrintSize = (item) => {
  const text = `${item?.name || ''} ${item?.description || ''}`.toLowerCase();
  return text.includes('mat/fame') || text.includes('mat/frame');
};

const MAT_TYPE_LABELS = {
  '8-ply': '8-Ply',
  'double-mat-4-ply': 'Double Mat 4-Ply',
};

const SelectField = ({ label, input, onChange, disabled }) => {
  if (!input) return null;
  const isLoading = input.isLoading && (!input.options || input.options.length === 0);

  return (
    <div className="input-container paper-type">
      <div className="label-container">
        <div className="div-block-150">
          <div className="sub-label-form">{label || input.label}</div>
        </div>
      </div>
      <div className="form-block-input w-form">
        <form className="form-input select-field">
          <select
            className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
            style={{ width: '100%' }}
            value={input.selectedValue || 'default'}
            disabled={disabled !== undefined ? disabled : !!input.isDisabled}
            onChange={onChange}
          >
            {isLoading ? (
              <option value="">Loading...</option>
            ) : (
              <option value="default">Choose One</option>
            )}
            {(input.options || [])
              .filter((o) => o?.value && o?.text && !o.text.endsWith('**'))
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.text}
                </option>
              ))}
          </select>
          <img
            src={ChevronIcon}
            alt=""
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              width: '16px',
              height: '16px',
            }}
          />
        </form>
      </div>
    </div>
  );
};

const PRODUCT_OPTIONS = MULTI_OPENING_PRODUCTS.map((p) => ({ value: p.id, text: p.name }));

// Returns product options filtered by opening type
const getFilteredProductOptions = (openingType) => {
  return MULTI_OPENING_PRODUCTS
    .filter((p) => {
      if (openingType === 'oval') return p.openingShape === 'oval';
      if (openingType === 'circle') return p.openingShape === 'circle';
      return true;
    })
    .map((p) => ({ value: p.id, text: p.name }));
};

const MultiOpeningMatFormInputs = ({ quantityRef }) => {
  const { form: globalForm, photos = [] } = useProductSelectionStore();

  const {
    form,
    setForm,
    selectedProduct,
    selectMultiOpeningApiProduct,
    processPaperTypeOptions,
    setPrintSizeLoading,
    processPrintSizeData,
    setFinishingServiceLoading,
    processFinishingCategoriesData,
    processFinishingServicesData,
    processFrameMatOptions,
    processFrameStainOptions,
    handleFinishingTypeChange,
    handleMatTypeChange,
    handleMatOpeningShapeChange,
    handleFrameNameChange,
    handleFrameStainChange,
    handleMatTypeFramedChange,
    handleMatOpeningShapeFramedChange,
    handleAcrylicChange,
  } = useMultiOpeningMatStore();

  const inputs = form?.inputs || {};
  const paperTypeId = inputs.paperType?.selectedValue;
  const printSizeId = inputs.printSize?.selectedValue;
  const frameTypeId = inputs.frameType?.selectedValue;
  const frameNameId = inputs.frameName?.selectedValue;
  const finishingCategoryIdForServices = frameTypeId || form?.meta?.finishingCategoryId;
  const isFramedFlow = inputs.finishingType?.selectedValue === 'framed';

  useEffect(() => {
    const options = globalForm?.inputs?.paperType?.options;
    if (!options || options.length === 0) return;
    processPaperTypeOptions(options);
  }, [globalForm?.inputs?.paperType?.options]);

  const printSizesQuery = useQuery({
    queryKey: ['printSizes', paperTypeId],
    queryFn: () => httpClient.get(`products/${paperTypeId}/print_sizes`),
    enabled: !!paperTypeId && paperTypeId !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => processPrintSizeData(response),
  });

  const paperTypeText = inputs.paperType?.selectedValueText || '';
  const multiOpeningApiOptions = useMemo(() => {
    const isCircleDeckle = isCircleDecklePaper(paperTypeText);
    const isOvalDeckle = isOvalDecklePaper(paperTypeText);

    return (printSizesQuery.data || [])
      .filter(isMultiOpeningPrintSize)
      .filter((item) => {
        if (!isCircleDeckle && !isOvalDeckle) return true;
        const staticProduct = findMultiOpeningProductByName(item.name);
        if (!staticProduct) return false;
        if (isCircleDeckle) return PRODUCTS_WITH_8X8.includes(staticProduct.id);
        return PRODUCTS_WITH_5X7.includes(staticProduct.id);
      })
      .map((item) => ({ value: item.guid, text: item.name }));
  }, [printSizesQuery.data, paperTypeText]);

  useEffect(() => {
    if (paperTypeId === 'default' || !paperTypeId) return;
    if (printSizesQuery.isLoading) setPrintSizeLoading(true);
  }, [paperTypeId, printSizesQuery.isLoading]);

  const finishingCategoriesQuery = useQuery({
    queryKey: ['finishingCategories', printSizeId],
    queryFn: () =>
      httpClient.get(`products/print_sizes/${printSizeId}/finishing_categories`),
    enabled: !!printSizeId && printSizeId !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => processFinishingCategoriesData(response),
  });

  useEffect(() => {
    if (printSizeId && printSizeId !== 'default' && finishingCategoriesQuery.isLoading) {
      setFinishingServiceLoading(true);
    }
  }, [printSizeId, finishingCategoriesQuery.isLoading]);

  const finishingServicesQuery = useQuery({
    queryKey: ['finishingServices', printSizeId, finishingCategoryIdForServices],
    queryFn: () =>
      httpClient.get(
        `products/print_sizes/${printSizeId}/finishing_categories/${finishingCategoryIdForServices}/finishing_services`,
      ),
    enabled: !!printSizeId && !!finishingCategoryIdForServices,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => processFinishingServicesData(response),
  });

  const frameMatsQuery = useQuery({
    queryKey: ['frameMats', printSizeId, finishingCategoryIdForServices],
    queryFn: () =>
      httpClient.get(
        `products/frame_mats?print_size_guid=${printSizeId}&finishing_category_guid=${finishingCategoryIdForServices}`,
      ),
    enabled:
      !!printSizeId &&
      !!finishingCategoryIdForServices &&
      (isFramedFlow ? !!frameNameId : true),
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => processFrameMatOptions(response),
  });

  useEffect(() => {
    if (frameMatsQuery.data) {
      processFrameMatOptions(frameMatsQuery.data);
    }
  }, [frameMatsQuery.data]);

  const frameMatRows = frameMatsQuery.data?.data ?? [];
  const apiMatTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          frameMatRows
            .map((row) => row.attributes?.matType)
            .filter((value) => MAT_TYPE_LABELS[value]),
        ),
      ).map((value) => ({ value, text: MAT_TYPE_LABELS[value] })),
    [frameMatRows],
  );
  const matTypeFramedOptions = apiMatTypeOptions;
  const isLoadingMatTypeFramed = !!frameNameId && frameMatsQuery.isLoading;

  const isLoadingFrameServices = !!frameTypeId && finishingServicesQuery.isFetching && !inputs.frameName;
  useQuery({
    queryKey: ['finishingDetails', frameNameId],
    queryFn: () => httpClient.get(`products/${frameNameId}/finishing_details`),
    enabled: !!frameNameId && !!inputs.frameStain,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => processFrameStainOptions(response),
  });

  useEffect(() => {
    if (
      selectedProduct &&
      inputs.printSize?.options?.length > 0 &&
      !inputs.printSize.selectedValue
    ) {
      const validSize = selectedProduct.validPrintSizes?.[0];
      if (!validSize) return;

      const matchingOption = inputs.printSize.options.find(
        (o) => o.text && o.text.startsWith(validSize)
      );

      if (matchingOption) {
        setForm((prev) => ({
          ...prev,
          meta: {
            ...prev.meta,
            xAspectRatio: matchingOption.xAspectRatio,
            yAspectRatio: matchingOption.yAspectRatio,
            finishingCategoryId: undefined,
            finishingServiceId: undefined,
            finishingDetailId: undefined,
          },
          inputs: {
            ...prev.inputs,
            printSize: {
              ...prev.inputs.printSize,
              selectedValue: matchingOption.value,
              selectedValueText: matchingOption.text,
              selectedValuePrice: matchingOption.price,
            },
            finishingType: {
              ...prev.inputs.finishingType,
              selectedValue: undefined,
              selectedValueText: undefined,
              isDisabled: false,
            },
          },
        }));
      }
    }
  }, [
    selectedProduct,
    inputs.printSize?.options,
    inputs.printSize?.selectedValue,
    setForm,
  ]);

  if (!form) return null;

  const handlePaperTypeChange = (e) => {
    const { value } = e.target;
    const { text } = e.target.options[e.target.selectedIndex];
    setForm((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        finishingCategoryId: undefined,
        finishingServiceId: undefined,
        finishingDetailId: undefined,
      },
      inputs: {
        paperType: { ...prev.inputs.paperType, selectedValue: value, selectedValueText: text },
        printSize: {
          label: 'Print Size',
          options: [],
          selectedValue: undefined,
          selectedValueText: undefined,
          selectedValuePrice: undefined,
          isLoading: value !== 'default',
          isDisabled: value === 'default',
        },
        finishingType: {
          label: 'Finishing Service',
          options: prev.inputs.finishingType?.options || [
            { value: 'matted', text: 'Matted' },
            { value: 'framed', text: 'Framed' },
          ],
          selectedValue: undefined,
          selectedValueText: undefined,
          isDisabled: true,
        },
      },
    }));
  };

  return (
    <div className={styles.printsFormStack}>
      {/* Quantity */}
      <div className="input-container quantity">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-quantity"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {/* Paper Type: selecting this drives the print sizes API call below */}
      {inputs.paperType && (
        <SelectField
          input={inputs.paperType}
          onChange={handlePaperTypeChange}
        />
      )}

      {/* Multi-Opening Product: populated from the print sizes API, filtered
          to the multi-opening (Frame/Mat) layouts, once a paper type is chosen */}
      <SelectField
        label="Multi-Opening Product"
        input={{
          options: multiOpeningApiOptions,
          selectedValue: selectedProduct?.id,
          isLoading: !!paperTypeId && paperTypeId !== 'default' && printSizesQuery.isLoading,
          isDisabled: !paperTypeId || paperTypeId === 'default',
        }}
        onChange={(e) => {
          const { value } = e.target;
          const apiItem = (printSizesQuery.data || []).find((item) => item.guid === value);
          selectMultiOpeningApiProduct(apiItem || null);
        }}
      />

      {/* Finishing Service: Matted / Framed */}
      {inputs.finishingType && !inputs.finishingType.isDisabled && (
        <SelectField
          input={inputs.finishingType}
          onChange={(e) => {
            const { value } = e.target;
            const { text } = e.target.options[e.target.selectedIndex];
            handleFinishingTypeChange(value, text);
          }}
        />
      )}

      {inputs.matType && (
        <SelectField
          input={inputs.matType}
          onChange={(e) =>
            handleMatTypeChange(e.target.value, e.target.options[e.target.selectedIndex].text)
          }
        />
      )}

      {inputs.matOpeningShape && !inputs.matOpeningShape.isHidden && (
        <SelectField
          input={inputs.matOpeningShape}
          onChange={(e) =>
            handleMatOpeningShapeChange(
              e.target.value,
              e.target.options[e.target.selectedIndex].text,
            )
          }
        />
      )}

      {inputs.frameType && (
        <SelectField
          label="Frame Name"
          input={inputs.frameType}
          onChange={(e) => {
            const { value } = e.target;
            const { text } = e.target.options[e.target.selectedIndex];
            setForm((prev) => ({
              ...prev,
              meta: {
                ...prev.meta,
                finishingCategoryId: value,
              },
              inputs: {
                ...prev.inputs,
                frameType: {
                  ...prev.inputs.frameType,
                  selectedValue: value,
                  selectedValueText: text,
                },
              },
            }));
          }}
        />
      )}

      {isLoadingFrameServices && (
        <>
          <SelectField label="Frame Wood" input={{ isLoading: true, options: [], isDisabled: true }} onChange={() => {}} />
        </>
      )}

      {inputs.frameName && (
        <SelectField
          label="Frame Wood"
          input={inputs.frameName}
          onChange={(e) => {
            const { value } = e.target;
            const { text } = e.target.options[e.target.selectedIndex];
            const opt = (inputs.frameName.options || []).find((o) => o.value === value);
            handleFrameNameChange(value, text, opt?.price);
          }}
        />
      )}

      {inputs.frameStain && !inputs.frameStain.isDisabled && (
        <SelectField
          input={inputs.frameStain}
          onChange={(e) =>
            handleFrameStainChange(
              e.target.value,
              e.target.options[e.target.selectedIndex].text,
            )
          }
        />
      )}

      {inputs.matTypeFramed && !inputs.matTypeFramed.isDisabled && (
        <SelectField
          input={{
            ...inputs.matTypeFramed,
            options: matTypeFramedOptions,
            isLoading: isLoadingMatTypeFramed,
          }}
          onChange={(e) =>
            handleMatTypeFramedChange(e.target.value, e.target.options[e.target.selectedIndex].text)
          }
        />
      )}

      {inputs.matOpeningShapeFramed &&
        !inputs.matOpeningShapeFramed.isDisabled &&
        !inputs.matOpeningShapeFramed.isHidden &&
        inputs.matTypeFramed?.selectedValue !== 'no-mat' && (
          <SelectField
            input={inputs.matOpeningShapeFramed}
            onChange={(e) =>
              handleMatOpeningShapeFramedChange(
                e.target.value,
                e.target.options[e.target.selectedIndex].text,
              )
            }
          />
      )}

      {inputs.acrylic && !inputs.acrylic.isDisabled && (
        <SelectField
          input={inputs.acrylic}
          onChange={(e) => {
            const { value } = e.target;
            const { text } = e.target.options[e.target.selectedIndex];
            const opt = (inputs.acrylic.options || []).find((o) => o.value === value);
            handleAcrylicChange(value, text, opt?.price);
          }}
        />
      )}
    </div>
  );
};

export default MultiOpeningMatFormInputs;
